import { useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import type { Session } from '@/lib/types';
import { supabase } from '@/lib/supabase';
import { fixtureOn } from '@/lib/fixture';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { SiteHeader } from '@/components/SiteHeader';
import { cn } from '@/lib/utils';

/**
 * La cornice.
 *
 * Due fasce, e dicono due cose diverse: in alto il marchio del prodotto
 * (`SiteHeader`, su ogni pagina), sotto il sottotitolo con l'identità di
 * questa sessione — l'ente **e** l'operatore collegato, due nomi e non uno,
 * per evitare l'errore che in un COC succede davvero: pubblicare dal computer
 * del collega credendo di essere se stessi. Ogni avviso porta una persona
 * negli atti, e quella persona deve essere leggibile prima di premere.
 */
export function Layout({ session }: { session: Session }) {
  // Il browser non riavvolge lo scroll quando a cambiare è solo la rotta di
  // una SPA: senza questo, chi apre un avviso dal fondo dello storico atterra
  // a metà della scheda, con il titolo fuori schermo.
  //
  // Le graffe non sono stile. Senza, l'arrow restituisce a React quello che
  // restituisce `scrollTo`, e React 19 considera funzione di pulizia QUALUNQUE
  // valore diverso da `undefined` torni da un effect: alla navigazione dopo lo
  // chiama. `scrollTo` per specifica torna `undefined`, ma non è codice nostro
  // — basta un'estensione del browser che lo avvolga e torni altro. Allora
  // React chiama un booleano dentro il commit, l'eccezione non ha un confine
  // che la fermi, e smonta l'intero root: console bianca a ogni click, e
  // contenuto solo dopo un ricaricamento.
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className="min-h-dvh">
      {fixtureOn && (
        <p className="bg-gialla px-5 py-1.5 text-center text-[13px] font-semibold text-ink">
          Dati finti — niente di quello che vedi o pubblichi qui è reale
        </p>
      )}

      <SiteHeader>
        <nav className="flex flex-1 items-center justify-end gap-1">
          <Tab to="/">Home</Tab>
          <Tab to="/storico">Storico</Tab>
        </nav>
      </SiteHeader>

      {/* Il sottotitolo della cornice: chi sta parlando e per conto di chi.
          È una fascia sua, non due righe appoggiate al contenuto — sopra c'è
          il marchio del prodotto, qui c'è l'identità di questa sessione, e la
          separazione fra le due cose è la ragione per cui questa riga si vede
          senza doverla cercare.

          I due nomi restano DUE, e l'ordine non è casuale: l'ente in evidenza
          perché è la firma che arriverà sui telefoni, la persona accanto
          perché l'errore che in un COC succede davvero è pubblicare dal
          computer del collega credendo di essere se stessi.

          Va a capo sotto i 480px invece di troncare: «Comune di Cinte Tesino»
          abbreviato a «Comune di Cinte…» toglie proprio la parola che
          distingue due Comuni vicini. */}
      <div className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-5 py-3">
          <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-3 gap-y-0.5">
            <p className="text-[17px] font-semibold leading-tight tracking-[-0.01em] text-ink">
              {session.display_name}
            </p>
            <p className="text-[14px] leading-tight text-ink3">
              {session.operator_name || 'Operatore'}
            </p>
          </div>
          {/* L'uscita sta QUI e non nell'intestazione: si esce da un'identità,
              non da un prodotto, e la riga che dice chi sei è il posto dove
              cercarla. Scritta a parole e non come icona — la porta con la
              freccia è un simbolo che metà delle persone legge come «entra». */}
          <Logout />
        </div>
      </div>

      <main className="mx-auto max-w-3xl px-5 pb-7 pt-7">
        {!session.is_active && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle />
            <AlertTitle>Canale sospeso</AlertTitle>
            <AlertDescription>
              {session.suspended_reason ||
                'La pubblicazione è sospesa per questo Comune.'}
            </AlertDescription>
          </Alert>
        )}
        {session.is_active && !session.console_enabled && (
          <Alert className="mb-6 border-arancione/50 bg-arancione/10">
            <AlertTriangle className="text-arancione" />
            <AlertTitle>Console chiusa</AlertTitle>
            <AlertDescription>
              Pubblicazione disattivata per manutenzione. Gli avvisi già
              pubblicati restano visibili in app.
            </AlertDescription>
          </Alert>
        )}
        <Outlet />
      </main>

      <footer className="mx-auto max-w-3xl px-5 pb-12 text-[13px] leading-relaxed text-ink4">
        Comunicazioni della Protezione Civile comunale. Non sostituiscono le
        allerte regionali: l’app è un canale complementare, senza garanzia di
        consegna.
      </footer>
    </div>
  );
}

/**
 * L'uscita, con la domanda prima.
 *
 * Non è una precauzione contro un danno — uscire non cancella niente — ma
 * contro la perdita di tempo nel momento peggiore: rientrare vuol dire un
 * magic link, la casella di posta, e un operatore che nel frattempo ha un COC
 * aperto e un avviso da mandare. La bozza in composizione sopravvive
 * (`sessionStorage`, vedi `lib/draft.ts`), l'accesso no.
 */
function Logout() {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="sm" className="shrink-0 text-ink3">
          Logout
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Sei sicuro di voler uscire?</AlertDialogTitle>
          <AlertDialogDescription>
            Per rientrare ti servirà un nuovo link di accesso via email. Gli
            avvisi già pubblicati restano visibili in app: uscire non ritira
            niente.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Resta collegato</AlertDialogCancel>
          <AlertDialogAction onClick={() => void supabase?.auth.signOut()}>
            Esci
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function Tab({ to, children }: { to: string; children: string }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        cn(
          'rounded-md px-3 py-1.5 text-[14px] font-medium transition-colors',
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-ink3 hover:bg-accent hover:text-ink',
        )
      }
    >
      {children}
    </NavLink>
  );
}
